import { Box, CardContent, Container, Grid } from "@mui/material";
import {
  IconCertificate,
  IconMessage,
  IconMoodSmileBeam,
  IconSoccerField,
  IconSparkles,
  IconStar,
  IconTools,
  IconUsers,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { SEO } from "../components/seo";
import Button from "../components/ui/Button";
import Text from "../components/ui/Text";
import { PAGE_SEO } from "../constants/seo";

const About = () => {
  const navigate = useNavigate();

  const coreValues = [
    {
      icon: <IconUsers size={24} />,
      title: "Community",
      description:
        "We believe in building and nurturing a strong community of casual sports players.",
    },
    {
      icon: <IconTools size={24} />,
      title: "Simplicity",
      description:
        "Our tools are designed to make organizing games as simple and straightforward as possible.",
    },
    {
      icon: <IconStar size={24} />,
      title: "Inclusivity",
      description:
        "We strive to create an inclusive platform where everyone, regardless of skill level, can participate and enjoy sports.",
    },
    {
      icon: <IconMessage size={24} />,
      title: "Transparency",
      description:
        "We ensure clear and honest communication, particularly in tracking attendance and payments.",
    },
    {
      icon: <IconSparkles size={24} />,
      title: "Innovation",
      description:
        "We continually innovate to provide the best solutions for game management and enhance user experience.",
    },
    {
      icon: <IconCertificate size={24} />,
      title: "Reliability",
      description:
        "Our users can depend on us for consistent and efficient service.",
    },
    {
      icon: <IconMoodSmileBeam size={24} />,
      title: "Fun",
      description:
        "Above all, we value the enjoyment and passion for sports, making every game a fun and engaging experience.",
    },
    {
      icon: <IconSoccerField size={24} />,
      title: "Collaboration",
      description:
        "We promote teamwork and cooperation, making it easy for players to come together and organize games seamlessly.",
    },
  ];

  return (
    <Box>
      <SEO
        title={PAGE_SEO.ABOUT.title}
        description={PAGE_SEO.ABOUT.description}
        keywords={PAGE_SEO.ABOUT.keywords}
      />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Hero Image Section */}
        <Box sx={{ mb: 3 }}>
          <Box
            component="img"
            src="/api/placeholder/800/300"
            alt="Soccer players in action"
            sx={{
              width: "100%",
              maxWidth: "600px",
              height: "300px",
              objectFit: "cover",
              borderRadius: "12px",
            }}
          />
        </Box>

        {/* Mission Section */}
        <Box sx={{ mb: 3 }}>
          <Text variant="pageHeader" sx={{ mb: "8px !important" }}>
            Our Mission
          </Text>
          <Text
            variant="paragraph"
            sx={{ textAlign: "center", maxWidth: "800px", mx: "auto" }}
          >
            Our mission is to make organizing casual sports games effortless
            with easy team management, attendance tracking, and payment handling
            tools
          </Text>
        </Box>

        {/* Vision Section */}
        <Box sx={{ mb: 3 }}>
          <Text variant="pageHeader" sx={{ mb: "8px !important" }}>
            Our Vision
          </Text>
          <Text
            variant="paragraph"
            sx={{ textAlign: "center", maxWidth: "800px", mx: "auto" }}
          >
            To become the leading platform for organizing casual sports games,
            connecting players globally and fostering community through seamless
            game management.
          </Text>
        </Box>
      </Container>

      {/* Core Values Section */}
      <Box sx={{ bgcolor: "#F5F5F5", px: 2, py: 3 }}>
        <Text
          variant="pageHeader"
          sx={{ mb: 4, fontSize: "24px", fontWeight: 700 }}
        >
          Our Core Values
        </Text>
        <Grid container spacing={3}>
          {coreValues.map((value, index) => (
            <Grid xs={12} sm={6} md={4} key={index}>
              <Box>
                <CardContent sx={{ p: 0 }}>
                  <Box
                    sx={{
                      backgroundColor: "black",
                      borderRadius: "50%",
                      width: 56,
                      height: 56,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                      color: "white",
                    }}
                  >
                    {value.icon}
                  </Box>
                  <Text
                    variant="paragraph2"
                    sx={{ mb: 1, fontSize: "20px", display: "block" }}
                  >
                    {value.title}
                  </Text>
                  <Text variant="paragraph">{value.description}</Text>
                </CardContent>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Box sx={{ textAlign: "center", mt: 8 }}>
          <Text variant="pageHeader" sx={{ mb: "40px !important" }}>
            Ready to get started?
          </Text>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              justifyContent: "center",
              maxWidth: "400px",
              mx: "auto",
            }}
          >
            <Button
              variant="secondary"
              onClick={() => navigate("/upcoming")}
              sx={{ flex: 1 }}
            >
              Browse upcoming games
            </Button>
            <Button
              variant="primary"
              onClick={() => navigate("/create-game")}
              sx={{ flex: 1 }}
            >
              Launch your game for free
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Call to Action Section */}
    </Box>
  );
};

export default About;
